import { Player, Position, DepthChartEntry, Sport } from '../types';
import { SPORT_RULESETS } from '../data/sportRulesets';
import { SPORT_POSITIONS } from '../data/sportsPositions';

interface RandomizeOptions {
  activeTab: string;
  sport: Sport;
  teamId: string;
  preserveColors?: boolean;
  ensureFairPlayTime?: boolean;
  absentPlayerIds?: string[]; // Players who are absent and should not be included
  selectedFormation?: string; // Current selected formation context
  standardProtectedPositions?: { [position: string]: string }; // For non-baseball sports
}

interface RandomizationResult {
  entries: DepthChartEntry[];
  statistics: {
    totalPositionsAssigned: number;
    playersAssigned: number;
    benchedPlayers: number;
    fairPlayTimeScore?: number;
  };
}

/**
 * Service for randomizing depth chart assignments
 * Handles sport-specific rules and ensures fair distribution
 */
export class DepthChartRandomizer {
  /**
   * Main randomization method - routes to appropriate algorithm based on sport and tab
   */
  static randomizeDepthChart(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions
  ): RandomizationResult {
    const { sport, activeTab } = options;
    
    console.log('🚨 [SIMPLIFIED] randomizeDepthChart called with:', { sport, activeTab });

    // Route to appropriate randomization algorithm - no more unit mapping complexity!
    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      console.log('[randomizeDepthChart] Routing to randomizeBaseballFielding');
      return this.randomizeBaseballFielding(players, positions, currentDepthChart, options);
    } else if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      console.log('🚨 [SIMPLIFIED] Going to randomizeLineupOrder - no mapping needed');
      return this.randomizeLineupOrder(players, positions, currentDepthChart, options);
    } else {
      console.log('[randomizeDepthChart] Routing to randomizeStandardDepthChart');
      return this.randomizeStandardDepthChart(players, positions, currentDepthChart, options);
    }
  }

  /**
   * Randomize baseball/softball fielding across innings
   */
  private static randomizeBaseballFielding(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions & { 
      protectedPositions?: { [inning: number]: { [position: string]: string } };
      teamInnings?: number;
    }
  ): RandomizationResult {
    const { sport, teamId, activeTab, preserveColors = false, protectedPositions = {}, teamInnings, absentPlayerIds = [], selectedFormation } = options;
    const ruleset = SPORT_RULESETS[sport];
    const defaultInnings = teamInnings || ruleset.defaultInnings || 7;
    const fieldingPositions = SPORT_POSITIONS[sport]['Defense'] || [];
    
    console.log('[randomizeBaseballFielding] Using activeTab (database unit):', activeTab);
    
    if (fieldingPositions.length === 0) {
      throw new Error(`No fielding positions found for ${sport}`);
    }

    // Filter out absent players
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    const newEntries: DepthChartEntry[] = [];
    
    // Track player usage per inning for fair distribution
    const playerInningUsage: Record<string, number[]> = {};
    const playerBenchInnings: Record<string, number[]> = {};
    
    availablePlayers.forEach(player => {
      playerInningUsage[player.id] = [];
      playerBenchInnings[player.id] = [];
    });

    // First, add ALL protected positions to track usage
    const protectedAssignments: { playerId: string; position: string; inning: number }[] = [];
    
    for (let inning = 1; inning <= defaultInnings; inning++) {
      const protectedForInning = protectedPositions[inning] || {};
      
      Object.entries(protectedForInning).forEach(([position, playerId]) => {
        // Only add if player is not absent
        if (!absentPlayerIds.includes(playerId)) {
          
          const entry: DepthChartEntry = {
            team_id: options.teamId,
            position: position,
            depth: inning,
            unit: activeTab,
            player_id: playerId,
            color: preserveColors ? (currentDepthChart.find(
              e => e.position === position && e.depth === inning && e.unit === activeTab
            )?.color || '') : ''
          };
          
          newEntries.push(entry);
          playerInningUsage[playerId].push(inning);
          protectedAssignments.push({ playerId, position, inning });
        }
      });
    }

    // Get positions that need to be filled (not protected) per inning
    const positionsToFill: { position: string; inning: number }[] = [];
    for (let inning = 1; inning <= defaultInnings; inning++) {
      const protectedForInning = protectedPositions[inning] || {};
      
      fieldingPositions.forEach(position => {
        if (!protectedForInning[position.key]) {
          positionsToFill.push({ position: position.key, inning });
        }
      });
    }

    // Advanced assignment algorithm with bench rules
    // This will consider ALL players (including those with protected assignments)
    const assignment = this.assignPlayersWithBenchRules(
      availablePlayers, // Use ALL available players, not just non-protected ones
      positionsToFill,
      defaultInnings,
      fieldingPositions.length,
      protectedAssignments // Pass protected assignments to consider them
    );

    // Add the assigned entries
    assignment.forEach(({ playerId, position, inning }) => {
      const entry: DepthChartEntry = {
        team_id: options.teamId,
        position: position,
        depth: inning,
        unit: activeTab,
        player_id: playerId,
        color: preserveColors ? (currentDepthChart.find(
          e => e.position === position && e.depth === inning && e.unit === activeTab
        )?.color || '') : ''
      };
      
      // Add formation field if sport uses formations
      const ruleset = SPORT_RULESETS[options.sport];
      if (ruleset.formations.enabled) {
        // Use the current selected formation from options
        entry.formation = selectedFormation || '';
        console.log('[randomizeBaseballFielding] Setting formation field:', entry.formation, 'for position:', entry.position);
      } else {
        console.log('[randomizeBaseballFielding] Sport does not use formations, omitting formation field');
      }
      
      newEntries.push(entry);
      if (!playerInningUsage[playerId]) playerInningUsage[playerId] = [];
      playerInningUsage[playerId].push(inning);
    });

    // Calculate bench innings for all players
    availablePlayers.forEach(player => {
      for (let inning = 1; inning <= defaultInnings; inning++) {
        if (!playerInningUsage[player.id].includes(inning)) {
          playerBenchInnings[player.id].push(inning);
        }
      }
    });

    // Calculate fair play time score
    const fairPlayTimeScore = this.calculateAdvancedFairPlayTimeScore(
      playerInningUsage, 
      playerBenchInnings, 
      defaultInnings
    );
    
    return {
      entries: newEntries,
      statistics: {
        totalPositionsAssigned: newEntries.length,
        playersAssigned: Object.keys(playerInningUsage).filter(playerId => 
          playerInningUsage[playerId].length > 0
        ).length,
        benchedPlayers: Math.max(0, availablePlayers.length - fieldingPositions.length),
        fairPlayTimeScore
      }
    };
  }

  /**
   * Advanced player assignment algorithm that follows bench rules:
   * 1. No player on bench for back-to-back innings
   * 2. No player on bench for more than 2 innings total
   * 3. Players should rotate through different positions
   */
  private static assignPlayersWithBenchRules(
    players: Player[],
    positionsToFill: { position: string; inning: number }[],
    totalInnings: number,
    positionsPerInning: number,
    protectedAssignments: { playerId: string; position: string; inning: number }[] = []
  ): { playerId: string; position: string; inning: number }[] {
    const assignment: { playerId: string; position: string; inning: number }[] = [];
    const playerBenchInnings: Record<string, number[]> = {};
    const playerPlayingInnings: Record<string, number[]> = {};
    const playerPositionHistory: Record<string, string[]> = {}; // Track which positions each player has played

    
    // Initialize tracking
    players.forEach(player => {
      playerBenchInnings[player.id] = [];
      playerPlayingInnings[player.id] = [];
      playerPositionHistory[player.id] = [];
    });

    // First, account for protected assignments
    protectedAssignments.forEach(({ playerId, position, inning }) => {
      playerPlayingInnings[playerId].push(inning);
      playerPositionHistory[playerId].push(position);
    });

    // Group positions by inning
    const positionsByInning: Record<number, string[]> = {};
    positionsToFill.forEach(({ position, inning }) => {
      if (!positionsByInning[inning]) positionsByInning[inning] = [];
      positionsByInning[inning].push(position);
    });

    // Create a rotation matrix to ensure players play different positions

    // Assign players inning by inning
    for (let inning = 1; inning <= totalInnings; inning++) {
      const positionsThisInning = positionsByInning[inning] || [];
      const playersNeeded = positionsThisInning.length;
      
      // Get players already assigned to this inning (protected)
      const protectedThisInning = protectedAssignments
        .filter(a => a.inning === inning)
        .map(a => a.playerId);
      

      
      if (playersNeeded === 0) continue;

      // Get players who must play this inning (due to bench rules)
      const mustPlayPlayers = players
        .map(p => p.id)
        .filter(playerId => {
          // Skip if already protected for this inning
          if (protectedThisInning.includes(playerId)) return false;
          
          const benchInnings = playerBenchInnings[playerId] || [];
          // Must play if they were benched last inning (no back-to-back bench)
          if (benchInnings.includes(inning - 1)) return true;
          // Must play if they already have 2 bench innings
          if (benchInnings.length >= 2) return true;
          return false;
        });

      // Get remaining available players, prioritized by playing time and position variety
      const otherPlayers = players
        .map(p => p.id)
        .filter(playerId => 
          !mustPlayPlayers.includes(playerId) && 
          !protectedThisInning.includes(playerId) // Don't include already protected players
        )
        .sort((a, b) => {
          // Prioritize players with fewer playing innings
          const aPlayingCount = playerPlayingInnings[a].length;
          const bPlayingCount = playerPlayingInnings[b].length;
          if (aPlayingCount !== bPlayingCount) return aPlayingCount - bPlayingCount;
          
          // Then prioritize players with fewer bench innings
          const aBenchCount = playerBenchInnings[a].length;
          const bBenchCount = playerBenchInnings[b].length;
          return aBenchCount - bBenchCount;
        });

      // Combine must-play players first, then fill remaining spots
      const playersToAssign = [...mustPlayPlayers];
      
      // Add other players to fill remaining spots
      const spotsRemaining = Math.max(0, playersNeeded - playersToAssign.length);
      playersToAssign.push(...otherPlayers.slice(0, spotsRemaining));
      


      // Assign players to positions with rotation logic
      const positionAssignments = this.assignPlayersToPositionsWithRotation(
        playersToAssign,
        positionsThisInning,
        playerPositionHistory,
        inning
      );

      // Add assignments
      positionAssignments.forEach(({ playerId, position }) => {
        assignment.push({
          playerId,
          position,
          inning
        });
        playerPlayingInnings[playerId].push(inning);
        playerPositionHistory[playerId].push(position);
      });

      // Mark benched players (those not playing this inning - not assigned AND not protected)
      const playingThisInning = new Set([
        ...playersToAssign.slice(0, positionsThisInning.length),
        ...protectedThisInning
      ]);
      
      players.forEach(player => {
        if (!playingThisInning.has(player.id)) {
          playerBenchInnings[player.id].push(inning);
        }
      });
    }



    return assignment;
  }

  /**
   * Assign players to positions ensuring rotation through different positions
   */
  private static assignPlayersToPositionsWithRotation(
    playerIds: string[],
    positions: string[],
    playerPositionHistory: Record<string, string[]>,
    _inning: number
  ): { playerId: string; position: string }[] {
    const assignments: { playerId: string; position: string }[] = [];
    const availablePositions = [...positions];
    const playersToAssign = [...playerIds];

    // Shuffle positions to add variety
    const shuffledPositions = this.shuffleArray(availablePositions);

    // Sort players by how many times they've played each position
    playersToAssign.sort((a, b) => {
      const aHistory = playerPositionHistory[a] || [];
      const bHistory = playerPositionHistory[b] || [];
      
      // Count unique positions played
      const aUniquePositions = new Set(aHistory).size;
      const bUniquePositions = new Set(bHistory).size;
      
      // Players who have played fewer unique positions go first
      return aUniquePositions - bUniquePositions;
    });

    // Assign each player to a position they haven't played recently
    playersToAssign.forEach((playerId, index) => {
      if (index >= shuffledPositions.length) return;

      const playerHistory = playerPositionHistory[playerId] || [];
      let assignedPosition: string | null = null;

      // Try to find a position the player hasn't played yet
      for (const position of shuffledPositions) {
        if (!playerHistory.includes(position) && 
            !assignments.some(a => a.position === position)) {
          assignedPosition = position;
          break;
        }
      }

      // If all positions have been played, pick the one played least recently
      if (!assignedPosition) {
        const positionCounts: Record<string, number> = {};
        
        // Count how many times each position has been played
        playerHistory.forEach((pos) => {
          positionCounts[pos] = (positionCounts[pos] || 0) + 1;
        });

        // Find available position with lowest count
        let minCount = Infinity;
        for (const position of shuffledPositions) {
          if (!assignments.some(a => a.position === position)) {
            const count = positionCounts[position] || 0;
            if (count < minCount) {
              minCount = count;
              assignedPosition = position;
            }
          }
        }
      }

      // Fallback: just assign any available position
      if (!assignedPosition) {
        assignedPosition = shuffledPositions.find(
          pos => !assignments.some(a => a.position === pos)
        ) || shuffledPositions[index];
      }

      assignments.push({
        playerId,
        position: assignedPosition
      });
    });

    return assignments;
  }

  /**
   * Calculate advanced fair play time score considering bench rules
   */
  private static calculateAdvancedFairPlayTimeScore(
    playerInningUsage: Record<string, number[]>,
    playerBenchInnings: Record<string, number[]>,
    totalInnings: number
  ): number {
    const playerIds = Object.keys(playerInningUsage);
    if (playerIds.length === 0) return 0;

    let score = 100;
    
    // Penalize violations of bench rules
    playerIds.forEach(playerId => {
      const benchInnings = playerBenchInnings[playerId] || [];
      const playingInnings = playerInningUsage[playerId] || [];
      
      // Penalty for more than 2 bench innings
      if (benchInnings.length > 2) {
        score -= (benchInnings.length - 2) * 15;
      }
      
      // Penalty for back-to-back bench innings
      for (let i = 0; i < benchInnings.length - 1; i++) {
        if (benchInnings[i + 1] === benchInnings[i] + 1) {
          score -= 20; // Heavy penalty for consecutive bench
        }
      }
      
      // Bonus for even playing time distribution
      const expectedPlayingTime = totalInnings * 0.6; // Roughly 60% playing time
      const actualPlayingTime = playingInnings.length;
      const deviation = Math.abs(actualPlayingTime - expectedPlayingTime);
      score -= deviation * 5;
    });

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Randomize standard depth chart (non-baseball fielding)
   */
  private static randomizeStandardDepthChart(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions
  ): RandomizationResult {
    const { sport, teamId, activeTab, preserveColors = false, absentPlayerIds = [], selectedFormation, standardProtectedPositions = {} } = options;
    const ruleset = SPORT_RULESETS[sport];
    const depthStrings = ruleset.depthStrings.defaultLabels;
    
    // Filter out absent players
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    const newEntries: DepthChartEntry[] = [];
    
    // Calculate total slots needed
    const totalSlots = positions.length * depthStrings.length;
    console.log('[randomizeStandardDepthChart] Total slots to fill:', totalSlots, 'Available players:', availablePlayers.length);
    
    // Create a master assignment list ensuring all players get used first
    const assignments: { player: Player; position: string; depth: number }[] = [];
    
    // Step 0: Handle protected positions first
    const protectedPlayerIds = Object.values(standardProtectedPositions);
    const protectedPositions = Object.keys(standardProtectedPositions);
    
    console.log('[randomizeStandardDepthChart] Protected positions:', protectedPositions);
    console.log('[randomizeStandardDepthChart] Protected players:', protectedPlayerIds);
    
    // Assign protected players to their positions at the first depth level
    protectedPositions.forEach(positionKey => {
      const playerId = standardProtectedPositions[positionKey];
      const player = availablePlayers.find(p => p.id === playerId);
      if (player) {
        assignments.push({
          player,
          position: positionKey,
          depth: depthStrings[0].id // Assign to first depth level
        });
        console.log(`[randomizeStandardDepthChart] Protected: ${player.firstName} ${player.lastName} to ${positionKey} (depth ${depthStrings[0].id})`);
      }
    });
    
    // Remove protected players from available pool for randomization
    const playersForRandomization = availablePlayers.filter(player => !protectedPlayerIds.includes(player.id));
    console.log('[randomizeStandardDepthChart] Players available for randomization:', playersForRandomization.length);
    
    // Step 1: Ensure every remaining player gets assigned at least once
    const shuffledPlayers = this.shuffleArray([...playersForRandomization]);
    const shuffledPositions = this.shuffleArray([...positions.filter(pos => !protectedPositions.includes(pos.key))]);
    const shuffledDepths = this.shuffleArray([...depthStrings]);
    
    console.log('[randomizeStandardDepthChart] Step 1: Assigning all', shuffledPlayers.length, 'players to ensure everyone gets used');
    
    shuffledPlayers.forEach((player, index) => {
      // Assign each player to a position/depth combination
      const positionIndex = index % shuffledPositions.length;
      const depthIndex = Math.floor(index / shuffledPositions.length) % shuffledDepths.length;
      
      const position = shuffledPositions[positionIndex];
      const depth = shuffledDepths[depthIndex];
      
      assignments.push({
        player,
        position: position.key,
        depth: depth.id
      });
      
      console.log(`[randomizeStandardDepthChart] Assigned ${player.firstName} ${player.lastName} to ${position.key} (depth ${depth.id})`);
    });
    
    // Step 2: Fill remaining slots by cycling through players again
    const assignedSlots = assignments.length;
    const remainingSlots = totalSlots - assignedSlots;
    
    console.log('[randomizeStandardDepthChart] Step 2: Filling', remainingSlots, 'remaining slots');
    
    if (remainingSlots > 0) {
      // Get all position/depth combinations that haven't been filled
      const allSlots: { position: string; depth: number }[] = [];
      positions.forEach(position => {
        depthStrings.forEach(depthString => {
          allSlots.push({ position: position.key, depth: depthString.id });
        });
      });
      
      // Remove already assigned slots
      const remainingSlotOptions = allSlots.filter(slot => 
        !assignments.some(assignment => 
          assignment.position === slot.position && assignment.depth === slot.depth
        )
      );
      
      // Shuffle remaining slots and fill them
      const shuffledRemainingSlots = this.shuffleArray(remainingSlotOptions);
      
      for (let i = 0; i < remainingSlots && i < shuffledRemainingSlots.length; i++) {
        const slot = shuffledRemainingSlots[i];
        const playerIndex = i % playersForRandomization.length;
        const player = playersForRandomization[playerIndex];
        
        assignments.push({
          player,
          position: slot.position,
          depth: slot.depth
        });
        
        console.log(`[randomizeStandardDepthChart] Filled remaining slot: ${player.firstName} ${player.lastName} to ${slot.position} (depth ${slot.depth})`);
      }
    }
    
    // Step 3: Convert assignments to depth chart entries
    assignments.forEach(assignment => {
      // Get existing color if preserving colors
      const existingEntry = currentDepthChart.find(
        entry => entry.position === assignment.position && 
                entry.depth === assignment.depth && 
                entry.unit === activeTab
      );
      
      const entry: DepthChartEntry = {
        team_id: teamId,
        position: assignment.position,
        depth: assignment.depth,
        unit: activeTab,
        player_id: assignment.player.id,
        color: preserveColors ? (existingEntry?.color || '') : ''
      };
      
      // Add formation field if sport uses formations
      if (ruleset.formations.enabled) {
        entry.formation = selectedFormation || '';
        console.log('[randomizeStandardDepthChart] Setting formation field:', entry.formation, 'for position:', entry.position, 'depth:', assignment.depth);
      } else {
        console.log('[randomizeStandardDepthChart] Sport does not use formations, omitting formation field');
      }
      
      newEntries.push(entry);
    });

    console.log('[randomizeStandardDepthChart] Final result:', newEntries.length, 'total entries, all', availablePlayers.length, 'players used');
    
    return {
      entries: newEntries,
      statistics: {
        totalPositionsAssigned: newEntries.length,
        playersAssigned: availablePlayers.length, // All available players are guaranteed to be used
        benchedPlayers: 0 // No one is benched since everyone gets assigned
      }
    };
  }

  /**
   * Randomize lineup order (for baseball/softball lineup tab)
   */
  static randomizeLineupOrder(
    players: Player[],
    positions: Position[],
    currentDepthChart: DepthChartEntry[],
    options: RandomizeOptions
  ): RandomizationResult {
    const { teamId, activeTab, preserveColors = false, absentPlayerIds = [], selectedFormation } = options;
    
    console.log('[randomizeLineupOrder] Using activeTab (database unit):', activeTab);
    
    // Filter out absent players; main roster (non-subs) bat first, then subs (each group shuffled)
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    const mainRoster = availablePlayers.filter(p => !(p as any).is_sub);
    const subs = availablePlayers.filter(p => (p as any).is_sub);
    const shuffledPlayers = [...this.shuffleArray(mainRoster), ...this.shuffleArray(subs)];
    const newEntries: DepthChartEntry[] = [];

    console.log('[randomizeLineupOrder] Available players:', availablePlayers.length);
    console.log('[randomizeLineupOrder] Positions array length:', positions.length);

    // For youth baseball/softball, everyone bats - create entries for all available players
    // Don't limit by positions array length
    shuffledPlayers.forEach((player, index) => {
      const battingOrder = index + 1; // 1-based batting order
      
      // Get existing color if preserving colors
      const existingEntry = currentDepthChart.find(
        entry => entry.position === battingOrder.toString() && 
                entry.depth === 1 && 
                entry.unit === activeTab
      );
      
      const entry: DepthChartEntry = {
        team_id: teamId,
        position: battingOrder.toString(), // Use batting order number as position
        depth: 1, // Lineup always uses depth 1
        unit: activeTab, // This should now be 'Offense' for baseball lineup
        player_id: player.id,
        color: preserveColors ? (existingEntry?.color || '') : ''
      };
      
      // Add formation field if sport uses formations
      const ruleset = SPORT_RULESETS[options.sport];
      if (ruleset.formations.enabled) {
        // Use the current selected formation from options
        entry.formation = selectedFormation || '';
        console.log('[randomizeLineupOrder] Setting formation field:', entry.formation, 'for batting order:', entry.position);
      } else {
        console.log('[randomizeLineupOrder] Sport does not use formations, omitting formation field');
      }
      
      newEntries.push(entry);
    });

    console.log('[randomizeLineupOrder] Created entries for', newEntries.length, 'players');
    console.log('[randomizeLineupOrder] First entry unit:', newEntries[0]?.unit);
    
    return {
      entries: newEntries,
      statistics: {
        totalPositionsAssigned: newEntries.length,
        playersAssigned: newEntries.length,
        benchedPlayers: 0 // No one is benched in the lineup - everyone bats
      }
    };
  }

  /**
   * Fisher-Yates shuffle algorithm for randomizing arrays
   */
  private static shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Validate randomization parameters
   */
  static validateRandomizationParams(
    players: Player[],
    positions: Position[],
    options: RandomizeOptions
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const { absentPlayerIds = [] } = options;

    if (!players || players.length === 0) {
      errors.push('No players available for randomization');
    }

    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));
    if (availablePlayers.length === 0) {
      errors.push('No available players for randomization (all players are absent)');
    }

    if (!positions || positions.length === 0) {
      errors.push('No positions available for randomization');
    }

    if (!options.sport || !SPORT_RULESETS[options.sport]) {
      errors.push('Invalid or unsupported sport');
    }

    if (!options.activeTab) {
      errors.push('Active tab is required');
    }

    if (!options.teamId) {
      errors.push('Team ID is required');
    }

    // Additional validation for baseball fielding with limited players
    if ((options.sport === 'Baseball' || options.sport === 'Softball') && options.activeTab === 'Fielding') {
      const fieldingPositions = SPORT_POSITIONS[options.sport]['Defense']?.length || 9;
      if (availablePlayers.length < fieldingPositions) {
        errors.push(`Need at least ${fieldingPositions} available players for fielding positions (currently have ${availablePlayers.length})`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get randomization preview information
   */
  static getRandomizationPreview(
    players: Player[],
    positions: Position[],
    options: RandomizeOptions & { teamInnings?: number }
  ): {
    willRandomize: number;
    willBench: number;
    strategy: string;
  } {
    const { sport, activeTab, absentPlayerIds = [], teamInnings } = options;
    const ruleset = SPORT_RULESETS[sport];

    // Filter out absent players for calculations
    const availablePlayers = players.filter(player => !absentPlayerIds.includes(player.id));

    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Fielding') {
      const fieldingPositions = SPORT_POSITIONS[sport]['Defense']?.length || 9;
      const defaultInnings = teamInnings || ruleset.defaultInnings || 7;
      
      return {
        willRandomize: availablePlayers.length,
        willBench: Math.max(0, availablePlayers.length - fieldingPositions),
        strategy: `Rotating ${Math.min(availablePlayers.length, fieldingPositions)} players across ${defaultInnings} innings${absentPlayerIds.length > 0 ? ` (${absentPlayerIds.length} absent)` : ''}`
      };
    }

    if ((sport === 'Baseball' || sport === 'Softball') && activeTab === 'Lineup') {
      return {
        willRandomize: availablePlayers.length,
        willBench: 0, // No one is benched in lineup - everyone bats
        strategy: `Randomizing batting order for all ${availablePlayers.length} players${absentPlayerIds.length > 0 ? ` (${absentPlayerIds.length} absent)` : ''}`
      };
    }

    // Standard depth chart
    const depthStrings = ruleset.depthStrings.defaultLabels;
    const totalSlots = positions.length * depthStrings.length;
    
    return {
      willRandomize: totalSlots, // All slots will be filled
      willBench: 0, // No one is benched since players can appear multiple times
      strategy: `Filling all ${totalSlots} positions (${positions.length} positions × ${depthStrings.length} depth levels) using ${availablePlayers.length} players${availablePlayers.length < totalSlots ? ' (players will appear multiple times)' : ''}${absentPlayerIds.length > 0 ? ` (${absentPlayerIds.length} absent)` : ''}`
    };
  }
} 